import { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { Search, User, MoreVertical } from "lucide-react";
import api from "../utils/api";

export default function UserList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [loadingStates, setLoadingStates] = useState({});
  const [loadingStatesReject, setLoadingStatesReject] = useState({});

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get("/api/user.php");
      console.log("Response:", response.data);
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && !event.target.closest(".relative")) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [activeDropdown]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleApprove = async (userId, approval) => {
    if (approval === 1)
      setLoadingStates((prev) => ({ ...prev, [userId]: true }));
    else setLoadingStatesReject((prev) => ({ ...prev, [userId]: true }));

    try {
      const response = await api.put("/api/user.php", {
        id: userId,
        api: "approve",
        approved: approval,
      });
      if (response.status === 200) {
        Swal.fire({
          title: approval === 1 ? "Approved!" : "Rejected!",
          text: response.data.message,
          icon: "success",
        });
        // Fetch updated user list instead of page reload
        await fetchUsers();
      }
    } catch (error) {
      console.error("Error approving user:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to update user status",
        icon: "error",
      });
    } finally {
      setLoadingStates((prev) => ({ ...prev, [userId]: false }));
      setLoadingStatesReject((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const filteredUsers = users.filter((user) =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen w-full p-4">
      <div className="container mx-auto p-4 bg-white border border-gray-700 rounded-lg shadow-sm sm:p-6 md:p-8">
        <div className="flex mb-4 justify-center sm:justify-start">
          <h5 className="text-lg sm:text-xl md:text-2xl font-bold leading-none text-gray-900 dark:text-white">
            Kuralattam Users
          </h5>
        </div>
        <div className="flow-root">
          {loading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : (
            <>
              <div className="relative mb-4">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Search className="w-5 h-5 text-gray-500" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 text-black bg-white"
                  placeholder="Search users..."
                />
              </div>
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredUsers.map((user) => (
                  <li key={user.id} className="py-2 sm:py-3 md:py-4">
                    <div className="flex items-center justify-between gap-2 sm:gap-4">
                      <div className="flex items-center flex-1 min-w-0">
                        <div className="shrink-0">
                          <User className="w-10 h-10 sm:w-12 sm:h-12 p-2 rounded-full border border-gray-100 text-gray-600" />
                        </div>
                        <div className="flex-1 min-w-0 ms-3 sm:ms-4">
                          <div>
                            <p className="text-sm sm:text-base font-medium text-gray-900 truncate">
                              {user.name}
                            </p>
                            <p className="text-xs sm:text-sm text-gray-500 truncate dark:text-gray-400">
                              {user.email}
                            </p>
                            <span
                              className={`inline-block mt-1 text-xs font-medium px-2.5 py-0.5 rounded-sm ${
                                user.Approved === 0
                                  ? "bg-red-100 text-red-800"
                                  : "bg-green-100 text-green-800"
                              }`}
                            >
                              {user.Approved === 0 ? "Pending" : "Approved"}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="shrink-0">
                        {/* Show 3-dot menu on mobile, buttons on larger screens */}
                        <div className="sm:hidden relative">
                          <button
                            onClick={() =>
                              setActiveDropdown(
                                activeDropdown === user.id ? null : user.id
                              )
                            }
                            className="p-1 hover:bg-gray-100 rounded-full"
                          >
                            <MoreVertical className="w-5 h-5 text-gray-500" />
                          </button>

                          {activeDropdown === user.id && (
                            <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-10 border border-gray-200">
                              {user.Approved === 0 && (
                                <button
                                  onClick={() => {
                                    handleApprove(user.id, 1);
                                    setActiveDropdown(null);
                                  }}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  Approve
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  handleApprove(user.id, 0);
                                  setActiveDropdown(null);
                                }}
                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                Reject
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Show buttons on larger screens */}
                        <div className="hidden sm:flex gap-2">
                          {user.Approved === 0 && (
                            <button
                              onClick={() => handleApprove(user.id, 1)}
                              disabled={loadingStates[user.id]}
                              className="px-3 py-1 text-sm font-medium text-white bg-green-500 rounded hover:bg-green-600 disabled:opacity-50 flex items-center gap-2"
                            >
                              {loadingStates[user.id] ? (
                                <>
                                  <svg
                                    className="animate-spin h-4 w-4 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    ></circle>
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                  </svg>
                                  Processing...
                                </>
                              ) : (
                                "Approve"
                              )}
                            </button>
                          )}
                          <button
                            onClick={() => handleApprove(user.id, 0)}
                            disabled={loadingStatesReject[user.id]}
                            className="px-3 py-1 text-sm font-medium text-white bg-red-500 rounded hover:bg-red-600 disabled:opacity-50 flex items-center gap-2"
                          >
                            {loadingStatesReject[user.id] ? (
                              <>
                                <svg
                                  className="animate-spin h-4 w-4 text-white"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  ></path>
                                </svg>
                                Processing...
                              </>
                            ) : (
                              "Reject"
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
